import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import '../styles/QRScanner.css';

const QRScanner = ({ onScan, onClose }) => {
  const [permissionStatus, setPermissionStatus] = useState('idle'); // idle, requesting, granted, denied, error
  const [errorMessage, setErrorMessage] = useState('');
  const scannerInstanceRef = useRef(null);
  const isMountedRef = useRef(true);

  // 1. Initial Permission Check
  useEffect(() => {
    isMountedRef.current = true;
    let stream = null;

    const checkPermission = async () => {
      setPermissionStatus('requesting');
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });

        // Release stream immediately, we just needed to verify access
        stream.getTracks().forEach(track => track.stop());

        if (isMountedRef.current) {
          setPermissionStatus('granted');
        }
      } catch (err) {
        console.error("Camera permission error:", err);
        if (isMountedRef.current) {
          setPermissionStatus('denied');
          if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
            setErrorMessage('Camera access denied. Please allow camera permissions in browser settings.');
          } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
            setErrorMessage('No camera found on this device.');
          } else {
            setErrorMessage('Error accessing camera: ' + err.message);
          }
        }
      }
    };

    checkPermission();

    return () => {
      isMountedRef.current = false;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // 2. Start Scanner when permission granted
  useEffect(() => {
    if (permissionStatus !== 'granted') return;

    const startScanner = async () => {
      try {
        const html5QrCode = new Html5Qrcode("qr-reader");
        scannerInstanceRef.current = html5QrCode;

        await html5QrCode.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            aspectRatio: 1.0,
          },
          (decodedText) => {
            // Success callback
            const categoryId = decodedText.split('/').pop();
            onScan(categoryId);
            // Optionally stop scanning here if you want to close immediately
            // html5QrCode.stop().catch(err => console.error("Failed to stop", err));
          },
          (errorMessage) => {
            // parse error, ignore it.
          }
        );
      } catch (err) {
        console.error("Failed to start scanner:", err);
        if (isMountedRef.current) {
          setPermissionStatus('error');
          setErrorMessage("Failed to start camera feed. Please refresh or try again.");
        }
      }
    };

    // Small delay to ensure DOM is rendered
    const timer = setTimeout(() => {
      startScanner();
    }, 100);

    return () => {
      clearTimeout(timer);
      if (scannerInstanceRef.current) {
        scannerInstanceRef.current.stop().then(() => {
          scannerInstanceRef.current.clear();
        }).catch(err => {
          console.error("Failed to stop scanner during cleanup", err);
        });
      }
    };
  }, [permissionStatus, onScan]);

  return (
    <div className="qr-scanner-overlay">
      <div className="qr-scanner-container">

        {permissionStatus === 'requesting' && (
          <div className="qr-status-message" style={{ color: 'white' }}>Requesting camera access...</div>
        )}

        {/* The container for the video feed */}
        <div id="qr-reader" className="qr-reader" style={{ display: permissionStatus === 'granted' ? 'block' : 'none' }} />

        {(permissionStatus === 'denied' || permissionStatus === 'error') && (
          <div className="qr-error" style={{ color: '#ff4444', padding: '20px', textAlign: 'center' }}>
            {errorMessage}
          </div>
        )}

        <button className="close-btn" onClick={onClose}>
          ✕
        </button>
      </div>
    </div>
  );
};

export default QRScanner;
